import { useState } from "react";
import { Sparkles, ThumbsDown } from "lucide-react";
import { ApiError } from "@/lib/api";
import { Badge, Button } from "@/components/ui/primitives";
import { useAuth } from "@/features/auth/auth-context";
import {
  useAcceptSuggestion,
  useClassify,
  useDismissSuggestion,
  useDuplicates,
  useKbSuggestions,
  useSuggestEngineer,
} from "./hooks";
import type { TriageSuggestion } from "./api";

const PRIORITY_TONE: Record<string, "muted" | "success" | "warning" | "danger"> = {
  low: "muted",
  medium: "muted",
  high: "warning",
  critical: "danger",
};

/**
 * AI assist for a ticket (staff only). Classification is advisory: it suggests
 * a category/priority/sentiment that a human applies or dismisses. Alongside it,
 * similar tickets, suggested KB articles and a ranked engineer shortlist.
 */
export function TicketAiPanel({ ticketId }: { ticketId: string }) {
  const { can } = useAuth();
  const [ran, setRan] = useState(false);
  const [triage, setTriage] = useState<TriageSuggestion | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [applied, setApplied] = useState(false);

  const classify = useClassify(ticketId);
  const accept = useAcceptSuggestion(ticketId);
  const dismiss = useDismissSuggestion();
  const duplicates = useDuplicates(ticketId, ran);
  const kb = useKbSuggestions(ticketId, ran);
  const engineers = useSuggestEngineer(ticketId, ran);

  if (!can("ticket:update")) return null;

  async function run() {
    setError(null);
    setApplied(false);
    try {
      const result = await classify.mutateAsync();
      setTriage(result);
      setRan(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "AI assist failed.");
    }
  }

  async function apply() {
    if (!triage) return;
    setError(null);
    try {
      await accept.mutateAsync(triage.suggestion_id);
      setApplied(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not apply.");
    }
  }

  async function reject() {
    if (!triage) return;
    await dismiss.mutateAsync(triage.suggestion_id).catch(() => {});
    setTriage(null);
  }

  return (
    <section className="rounded-md border border-border">
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Sparkles className="h-4 w-4 text-primary" aria-hidden="true" />
          AI assist
        </div>
        <Button variant="outline" onClick={() => void run()} disabled={classify.isPending}>
          {ran ? "Re-run" : "Analyse"}
        </Button>
      </div>

      <div className="space-y-3 p-3">
        {error && (
          <div role="alert" className="rounded-md bg-destructive/10 px-3 py-1.5 text-xs text-destructive">
            {error}
          </div>
        )}

        {!ran && (
          <p className="text-xs text-muted-foreground">
            Suggest a category, priority and sentiment, find similar tickets, matching KB articles
            and the best-placed engineer.
          </p>
        )}

        {triage && (
          <div className="rounded-md border border-border p-3">
            <div className="mb-2 flex flex-wrap items-center gap-2 text-sm">
              <span className="font-medium">Suggested triage</span>
              {triage.category_name && <Badge tone="muted">{triage.category_name}</Badge>}
              <Badge tone={PRIORITY_TONE[triage.priority] ?? "muted"}>{triage.priority}</Badge>
              <Badge tone="muted">{triage.sentiment}</Badge>
              <span className="ml-auto text-xs text-muted-foreground">
                {Math.round(triage.confidence * 100)}% · {triage.provider}
              </span>
            </div>
            <p className="mb-2 text-xs text-muted-foreground">{triage.reason}</p>
            {applied ? (
              <p className="text-xs font-medium text-sla-ok">Applied to the ticket.</p>
            ) : (
              <div className="flex gap-2">
                <Button onClick={() => void apply()} disabled={accept.isPending}>
                  Apply
                </Button>
                <Button variant="ghost" onClick={() => void reject()}>
                  <ThumbsDown className="h-4 w-4" aria-hidden="true" />
                  Dismiss
                </Button>
              </div>
            )}
          </div>
        )}

        {ran && (
          <div className="grid gap-3 sm:grid-cols-2">
            <Group title="Similar tickets">
              {(duplicates.data ?? []).length === 0 ? (
                <Empty>No similar tickets.</Empty>
              ) : (
                (duplicates.data ?? []).map((d) => (
                  <Row key={d.id} primary={d.title} secondary={d.ticket_number} score={d.similarity} />
                ))
              )}
            </Group>

            <Group title="Suggested articles">
              {(kb.data ?? []).length === 0 ? (
                <Empty>No matching articles.</Empty>
              ) : (
                (kb.data ?? []).map((a) => (
                  <Row key={a.id} primary={a.title} secondary={a.summary ?? ""} score={a.score} />
                ))
              )}
            </Group>

            <Group title="Suggested engineers">
              {(engineers.data ?? []).length === 0 ? (
                <Empty>No engineers available.</Empty>
              ) : (
                (engineers.data ?? []).map((e) => (
                  <Row key={e.user_id} primary={e.name} secondary={e.reason} score={e.score} />
                ))
              )}
            </Group>
          </div>
        )}
      </div>
    </section>
  );
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-border p-2">
      <div className="mb-1 px-1 text-xs font-medium uppercase text-muted-foreground">{title}</div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Row({ primary, secondary, score }: { primary: string; secondary: string; score: number }) {
  return (
    <div className="flex items-baseline gap-2 rounded-md px-1 py-1 text-sm">
      <span className="truncate font-medium">{primary}</span>
      <span className="ml-auto shrink-0 text-xs tabular-nums text-muted-foreground">
        {secondary ? `${secondary} · ` : ""}
        {score}
      </span>
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="px-1 text-xs text-muted-foreground">{children}</p>;
}
